import {
  Grid, TextInput, Select, Button,
} from '@mantine/core';
import { Page } from '@/components';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import validate from '@/helpers/validations';
import DeleteButton from '@/components/DeleteButton';
import Loading from '@/components/Loading';
import { useTranslation } from 'react-i18next';
import { getMutationResult } from '@/helpers/query';
import { useGetCurrentMixDataMutation, useGetTranchesDataMutation, useAddCurrentMixDataMutation } from '@/services/api';
import { IconZoomExclamation } from '@tabler/icons-react';
import classes from './style.module.css';

const Mixes = () => {
  const { t } = useTranslation();
  const [tranchesLoading, setTranchesLoading] = useState(false);
  const [getCurrentMixDataConfig] = useGetCurrentMixDataMutation();
  const [tranchesDataMutationConfig] = useGetTranchesDataMutation();
  const [addCurrentMixDataMutationConfig] = useAddCurrentMixDataMutation();
  const [loading, { open, close }] = useDisclosure(false);
  const [isError, setIsError] = useState(false);

  const fieldValidations = {
    rate: (value) => (validate(value, {
      required: t('globals.validations.required'),
      decimalNumber: t('globals.validations.number'),
    })),
  };

  const form = useForm({
    initialValues: {
      tranches: [],
      mixes: [
        {
          priority: '',
          risk: '',
          tranches: [
            {
              tranchName: '', // Default empty tranche name
              amount: '', // Default empty amount
            },
          ],
        },
      ],
    },
    validateInputOnBlur: true,
    validate: {},
  });

  const fetchData = async () => {
    setTranchesLoading(true);
    try {
      const {
        data: tranchesDataResult,
        isError: isErrorInTranchesData,
      } = await getMutationResult({}, tranchesDataMutationConfig);

      if (isErrorInTranchesData) {
        setIsError(true);
        setTranchesLoading(false);
        return;
      }
      const { tranches } = tranchesDataResult;

      const {
        data: currentMixData,
        isError: isErroIncurrentMixData,
      } = await getMutationResult({}, getCurrentMixDataConfig);

      if (isErroIncurrentMixData) {
        setIsError(true);
        setTranchesLoading(false);
        return;
      }

      const { data } = currentMixData;

      // Map tranches to the required structure
      const tranchesData = tranches.map((tranche) => ({
        id: tranche._id,
        hebrew: tranche.hebrew,
      }));

      // Map mixes to the required structure
      const mixesData = data.map((mix) => ({
        priority: mix.priority,
        risk: mix.risk,
        tranches: mix.tranches.map((tranche) => ({
          tranchName: tranche.trancheId, // Set trancheId as the selected value
          amount: tranche.percentageForMix, // Set percentageForMix as the amount
        })),
      }));

      // Set the form values
      form.setFieldValue('tranches', tranchesData);
      form.setFieldValue('mixes', mixesData);
    } catch (error) {
      // empty

    }
    setTranchesLoading(false);
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    // Validate the form fields
    if (form.validate().hasErrors) {
      return;
    }
    // Ensure there is at least one mix
    if (form.values.mixes.length === 0) {
      notifications.show({
        title: 'המידע לא נשמר בהצלחה',
        message: 'יש להזין לפחות תמהיל אחד',
        color: 'red',
      });
      return;
    }

    // Validate that the sum of amounts in each mix's tranches equals 100
    const invalidMix = form.values.mixes.find((mix, mixIdx) => {
      const totalAmount = mix.tranches.reduce(
        (sum, tranche) => sum + Number(tranche.amount || 0),
        0,
      );
      if (totalAmount !== 100) {
        notifications.show({
          title: 'המידע לא נשמר בהצלחה',
          message: `האחוז של תמהיל מספר ${mixIdx + 1} חייב להיות שווה ל-100%. כרגע הוא ${totalAmount}%.`,
          color: 'red',
        });
        return true;
      }
      return false;
    });

    if (invalidMix) {
      return;
    }
    open();
    try {
      // Prepare the data for submission
      const mixesData = form.values.mixes.map((mix, idx) => ({
        priority: idx,
        risk: idx,
        tranches: mix.tranches.map((tranche) => ({
          trancheId: tranche.tranchName, // Use tranchName as trancheId
          percentageForMix: tranche.amount, // Use amount as percentageForMix
        })),
      }));

      const {
        data,
        isError: addCurrentMixDataIsError,
      } = await getMutationResult({ data: mixesData }, addCurrentMixDataMutationConfig);

      // eslint-disable-next-line no-unused-vars
      const response = data;

      if (addCurrentMixDataIsError) {
        open();
        notifications.show({
          title: 'שגיאה בשמירת הנתונים',
          message: 'הנתונים לא נשמרו בהצלחה',
          color: 'red',
        });
        return;
      }

      notifications.show({
        title: 'המידע נשמר בהצלחה',
        message: 'המידע נשמר בהצלחה',
      });
    } catch (error) {
      notifications.show({
        title: 'המידע לא נשמר בהצלחה',
        message: 'המידע לא נשמר בהצלחה',
        color: 'red',
      });
    }
    close();
  };

  if (tranchesLoading) return (<Loading />);
  if (isError) {
    return (
      <div className={classes.errorContainer}>
        <h2>שגיאה בטעינת הנתונים</h2>
        <IconZoomExclamation size={16} />
      </div>
    );
  }

  return (
    <>
      <Page.Head
        title={t('pages.settings.pages.mixes.title')}
        subtitle={t('pages.settings.pages.mixes.description')}
      />
      <div className={classes.container}>
        <div className={classes.mixesWrapper}>
          {form.values.mixes?.map((mix, mixIdx) => (
            <div key={mixIdx} className={classes.mixBox}>
              <div className={classes.mixesHeader}>
                <h2>תמהיל {mixIdx + 1}
                  {mixIdx === 0 && ' - סיכון נמוך'}
                  {mixIdx === 1 && ' - סיכון נמוך בינוני'}
                  {mixIdx === 2 && ' - סיכון בינוני'}
                  {mixIdx === 3 && ' - סיכון בינוני גבוהה'}
                  {mixIdx === 4 && ' - סיכון גבוהה'}
                </h2>
                {form.values.mixes.length > 1 && ( // Show delete button only if more than one mix
                  <DeleteButton onClick={() => {
                    const updatedMixes = [...form.values.mixes];
                    updatedMixes.splice(mixIdx, 1);
                    form.setFieldValue('mixes', updatedMixes);
                  }} />
                )}
              </div>
              <div className={classes.tranchesWrapper}>
                {mix.tranches.map((tranche, trancheIdx) => (
                  <div key={trancheIdx} className={classes.trancheBox}>
                    <div className={classes.trancheHeader}>
                      <h3>מסלול {trancheIdx + 1}</h3>
                      {mix.tranches.length > 1 && (
                        <DeleteButton onClick={() => {
                          const updatedTranches = [...mix.tranches];
                          updatedTranches.splice(trancheIdx, 1);
                          form.setFieldValue(
                            `mixes.${mixIdx}.tranches`,
                            updatedTranches,
                          );
                        }} />
                      )}
                    </div>
                    <Grid>
                      <Grid.Col span={12}>
                        <Select
                          label="שם מסלול"
                          placeholder="בחר מסלול"
                          data={form.values.tranches.map((tranchOption) => ({
                            value: tranchOption.id,
                            label: tranchOption.hebrew,
                          }))}
                          value={tranche.tranchName}
                          onChange={(value) => form.setFieldValue(
                            `mixes.${mixIdx}.tranches.${trancheIdx}.tranchName`,
                            value,
                          )}
                          required
                        />
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <TextInput
                          label="מסלול מומלץ"
                          placeholder="סכום"
                          value={tranche.amount}
                          onChange={(e) => form.setFieldValue(
                            `mixes.${mixIdx}.tranches.${trancheIdx}.amount`,
                            e.target.value,
                          )}
                          error={fieldValidations.rate(tranche.amount)}
                          required
                        />
                      </Grid.Col>
                    </Grid>
                  </div>
                ))}
                <Button
                  onClick={() => form.setFieldValue(`mixes.${mixIdx}.tranches`, [
                    ...mix.tranches,
                    { amount: '', tranchName: '' },
                  ])}
                >
                  הוסף מסלול
                </Button>
              </div>
            </div>
          ))}
          {form.values.mixes.length < 5 && <Button
            onClick={() => form.setFieldValue('mixes', [
              ...form.values.mixes,
              { priority: '', tranches: [] },
            ])
            }
          >
            הוסף תמהיל
          </Button>}
        </div>
        <div className={classes.buttonsWrapper}>
          <Button loading={loading} onClick={handleSubmit} className={classes.submitButton}>
            שמירה
          </Button>
        </div>
      </div>
    </>
  );
};

export default Mixes;
